'use client';

import React, { useState, JSXElementConstructor, createContext, useContext } from 'react';
import { cn } from '../../helpers/utils';

// Context
type TabsOrientation = 'horizontal' | 'vertical';

interface TabsContextType {
	selectedValue: number | string;
	orientation: TabsOrientation;
}

const TabsContext = createContext<TabsContextType | undefined>(undefined);

export const TabsProvider = ({
	children,
	value,
	orientation,
}: {
	children: React.ReactNode;
	value: number;
	orientation: TabsOrientation;
}) => <TabsContext.Provider value={{ selectedValue: value, orientation }}>{children}</TabsContext.Provider>;

export const useTabsContext = () => {
	const context = useContext(TabsContext);
	if (!context) throw new Error('useTabsContext must be used within TabsProvider');
	return context;
};

// Tab component
interface TabProps {
	value: number | string;
	label: string | React.ReactNode;
	isSelected?: boolean;
	onClick?: (_value: number) => void;
	disabled?: boolean;
	children?: React.ReactNode;
}

const TabVariantContext = createContext<'pill' | 'underline'>('pill');

const Tab = ({ value, label, isSelected, onClick, disabled }: TabProps) => {
	const variant = useContext(TabVariantContext);

	return (
		<button
			onClick={() => onClick?.(value)}
			disabled={disabled}
			className={cn(
				'px-4 py-2 text-sm font-medium transition-all select-none',
				'!outline-none',
				variant === 'underline'
					? cn(
							'border-b-2 rounded-none -mb-px',
							isSelected ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-900',
						)
					: cn(
							'rounded-md',
							isSelected
								? 'bg-white text-gray-900 border border-gray-300 shadow-sm'
								: 'text-gray-500 border border-transparent hover:text-gray-900',
						),
				disabled && 'opacity-50 cursor-not-allowed',
			)}
		>
			{label}
		</button>
	);
};
Tab.displayName = 'Tab';

// TabList component
interface TabListProps {
	children: React.ReactNode;
	className?: string;
}

const TabList = ({ children, className }: TabListProps) => {
	const { orientation } = useTabsContext();
	return (
		<div
			className={cn('flex border-gray-200', orientation === 'vertical' ? 'flex-col space-y-1' : 'space-x-1', className)}
		>
			{children}
		</div>
	);
};
TabList.displayName = 'TabList';

// TabPanel component
interface TabPanelProps {
	value: number;
	selectedValue?: number;
	children: React.ReactNode;
	className?: string;
}

const TabPanel = ({ value, selectedValue, children, className }: TabPanelProps) => {
	const { orientation } = useTabsContext();

	if (value !== selectedValue) {
		return null;
	}

	return <div className={cn('p-4', orientation === 'vertical' ? 'ml-4' : 'mt-4', className)}>{children}</div>;
};
TabPanel.displayName = 'TabPanel';

// Helper
interface NamedComponent {
	displayName?: string;
}

const isNamedComponent = (
	type: string | JSXElementConstructor<unknown>,
): type is JSXElementConstructor<unknown> & NamedComponent => {
	return typeof type !== 'string' && 'displayName' in type;
};

// Tabs component
interface TabsProps {
	defaultValue?: number | string;
	value?: number | string;
	onChange?: ((_value: number) => void) | ((_value: string) => void);
	children: React.ReactNode;
	className?: string;
	actions?: React.ReactNode;
	orientation?: TabsOrientation;
	variant?: 'pill' | 'underline';
}

interface TabsComponent extends React.FC<TabsProps> {
	Tab: typeof Tab;
	TabList: typeof TabList;
	TabPanel: typeof TabPanel;
}

const Tabs: TabsComponent = ({
	defaultValue = 0,
	value: controlledValue,
	onChange,
	children,
	className,
	actions,
	orientation = 'horizontal',
	variant = 'pill',
}: TabsProps) => {
	const [internalValue, setInternalValue] = useState(defaultValue);
	const value = controlledValue ?? internalValue;
	const isVertical = orientation === 'vertical';

	const handleTabClick = (tabValue: number) => {
		if (controlledValue === undefined) {
			setInternalValue(tabValue);
		}
		onChange?.(tabValue);
	};

	const enhancedChildren = React.Children.map(children, (child) => {
		if (!React.isValidElement(child)) {
			return child;
		}

		if (isNamedComponent(child.type) && child.type.displayName === 'TabList') {
			const tabListChildren = React.Children.map(child.props.children, (tabChild) => {
				if (
					!React.isValidElement<TabProps>(tabChild) ||
					!isNamedComponent(tabChild.type) ||
					tabChild.type.displayName !== 'Tab'
				) {
					return tabChild;
				}

				return React.cloneElement(tabChild, {
					isSelected: tabChild.props.value === value,
					onClick: handleTabClick,
					value: tabChild.props.value,
					children: tabChild.props.children,
				});
			});

			let wrapperClass = variant === 'underline' ? 'flex items-end' : 'flex items-center bg-[#EDF0F2] rounded-md p-1';

			if (isVertical) {
				wrapperClass = 'flex flex-col items-stretch self-start';
			} else {
				wrapperClass += ' justify-between';
			}

			return (
				<div className={wrapperClass}>
					{React.cloneElement(child as React.ReactElement<TabListProps>, {
						children: tabListChildren,
					})}
					{actions && <div className="flex items-center gap-2">{actions}</div>}
				</div>
			);
		}

		if (isNamedComponent(child.type) && child.type.displayName === 'TabPanel') {
			return React.cloneElement(child as React.ReactElement<TabPanelProps>, {
				...child.props,
				selectedValue: value,
			});
		}

		return child;
	});

	return (
		<div className={cn('z-10', isVertical && 'flex', className)}>
			<TabVariantContext.Provider value={variant}>
				<TabsProvider value={value ?? 0} orientation={orientation}>
					{enhancedChildren}
				</TabsProvider>
			</TabVariantContext.Provider>
		</div>
	);
};

Tabs.Tab = Tab;
Tabs.TabList = TabList;
Tabs.TabPanel = TabPanel;

export { Tabs, Tab, TabList, TabPanel, type TabsOrientation };
export default Tabs;
